import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';

const TONES = [
  { value: 'consultative', label: 'Consultative', desc: 'Diagnostic, earned authority' },
  { value: 'direct', label: 'Direct', desc: 'Confident, no fluff' },
  { value: 'warm', label: 'Warm', desc: 'Human, respectful' },
  { value: 'formal', label: 'Formal', desc: 'Polished, considered' },
  { value: 'human', label: 'Human', desc: 'Warm, plain, personal' },
];
const INTENTS = [
  { value: 'sell', label: 'Sell', desc: 'Clear offer, asks for time' },
  { value: 'nurture', label: 'Nurture', desc: 'Build rapport, plant seeds' },
  { value: 'info', label: 'Info', desc: 'Share value, no ask' },
];
const LENGTHS = [
  { value: 'short', label: 'Short', desc: '60 to 90 words' },
  { value: 'medium', label: 'Medium', desc: '100 to 140 words' },
  { value: 'long', label: 'Long', desc: '150 to 200 words' },
];
const MEETING_ASKS = [
  { value: 'specific', label: 'Specific', desc: 'Proposes a 20-minute window' },
  { value: 'soft', label: 'Soft', desc: 'Leaves the door open' },
  { value: 'none', label: 'None', desc: 'Never asks for a meeting' },
];

export default function CrmSequenceSettings({ level = 'contact', id, contactId }) {
  const recId = id != null ? id : contactId;
  const table = level === 'client' ? 'clients' : level === 'company' ? 'companies' : 'contacts';
  const isContact = table === 'contacts';
  const isClient = table === 'clients';
  const levelName = isClient ? 'client' : table === 'companies' ? 'company' : 'contact';
  const base = `${API_BASE}/${table}/${recId}`;
  const { getToken } = useAuth();
  const [config, setConfig] = useState(null);
  const [canWrite, setCanWrite] = useState(true);
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(level === 'client');
  const [draft, setDraft] = useState({});
  const [overrides, setOverrides] = useState(null);

  const load = useCallback(async () => {
    if (!recId) return;
    setLoading(true);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const r = await fetch(`${base}/sequence-config`, { headers });
      if (r.ok) {
        const data = await r.json();
        setConfig(data.config);
        setDraft(data.config || {});
        setCanWrite(data.can_write !== false);
      }
      if (isContact) {
        const rr = await fetch(`${base}/resolved-sequence-config`, { headers });
        if (rr.ok) setResolved(await rr.json());
      }
      if (isClient) {
        const ro = await fetch(`${base}/sequence-config/overrides`, { headers });
        if (ro.ok) setOverrides(await ro.json());
      }
    } catch (e) {
      toast.error('Failed to load sequence config');
    }
    setLoading(false);
  }, [getToken, base, recId, isContact, isClient]);

  useEffect(() => { load(); }, [load]);

  async function refreshResolved(token) {
    if (!isContact) return;
    const rr = await fetch(`${base}/resolved-sequence-config`, { headers: { Authorization: `Bearer ${token}` } });
    if (rr.ok) setResolved(await rr.json());
  }

  async function save() {
    setSaving(true);
    try {
      const token = await getToken();
      const r = await fetch(`${base}/sequence-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ config: draft })
      });
      if (!r.ok) { const err = await r.json().catch(() => ({})); toast.error(err.error || 'Failed to save'); setSaving(false); return; }
      const data = await r.json();
      setConfig(data.config);
      toast.success('Sequence settings saved');
      await refreshResolved(token);
    } catch (e) {
      toast.error('Failed to save sequence config');
    }
    setSaving(false);
  }

  async function clearOverride() {
    if (!window.confirm(`Clear the ${levelName}-level settings? Sequences will use the inherited default.`)) return;
    setSaving(true);
    try {
      const token = await getToken();
      const r = await fetch(`${base}/sequence-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ config: null })
      });
      if (r.ok) {
        setConfig(null); setDraft({});
        toast.success('Override cleared');
        await refreshResolved(token);
      } else { toast.error('Failed to clear'); }
    } catch (e) { toast.error('Failed to clear'); }
    setSaving(false);
  }

  async function clearChildOverrides() {
    if (!overrides) return;
    const n = (overrides.contacts || 0) + (overrides.companies || 0);
    if (!window.confirm(`Clear the own settings on ${n} record${n === 1 ? '' : 's'} so they all follow this client's settings?`)) return;
    setSaving(true);
    try {
      const token = await getToken();
      const r = await fetch(`${base}/sequence-config/overrides`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Failed to clear');
      toast.success(`${data.contacts_cleared || 0} contacts and ${data.companies_cleared || 0} companies now follow client settings`);
      setOverrides({ contacts: 0, companies: 0 });
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  }

  const rc = (resolved && resolved.config) || {};
  const effEmail = draft.email_count !== undefined ? draft.email_count : (rc.email_count !== undefined ? rc.email_count : 5);
  const effLi = draft.linkedin_count !== undefined ? draft.linkedin_count : (rc.linkedin_count !== undefined ? rc.linkedin_count : 5);
  const stepCount = Math.max(effEmail, effLi, 1);
  function toggleStep(n) {
    const cur = Array.isArray(draft.sales_steps) ? draft.sales_steps : [];
    const next = cur.includes(n) ? cur.filter(x => x !== n) : [...cur, n].sort((a, b) => a - b);
    setDraft({ ...draft, sales_steps: next });
  }

  function summary() {
    if (loading) return 'Loading…';
    if (!config) return resolved ? `Inherited from ${resolved.source}` : (isClient ? 'Not set, using account or system default' : 'Using inherited default');
    const parts = [];
    if (config.intent) parts.push(config.intent);
    if (config.email_count !== undefined) parts.push(`${config.email_count} email${config.email_count !== 1 ? 's' : ''}`);
    if (config.linkedin_count !== undefined) parts.push(`${config.linkedin_count} LinkedIn`);
    return parts.length > 0 ? `Custom: ${parts.join(', ')}` : 'Custom';
  }

  return (
    <div className="crmr-seqcfg">
      <div className="crmr-seqcfg-head" onClick={() => setExpanded(!expanded)}>
        <div>
          <div className="crmr-seqcfg-title">{isClient ? 'Sequence settings for this client' : 'Sequence settings'}</div>
          <div className="crmr-seqcfg-sub">{summary()}</div>
        </div>
        <span className="crmr-seqcfg-chev">{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div className="crmr-seqcfg-body">
          {isClient && overrides && (overrides.contacts > 0 || overrides.companies > 0) && (
            <div className="crmr-seqcfg-resolve">
              {overrides.contacts} contact{overrides.contacts === 1 ? '' : 's'} and {overrides.companies} compan{overrides.companies === 1 ? 'y' : 'ies'} have their own settings and won't follow these.
              {canWrite && <>{' '}<button type="button" className="crmr-act crmr-act-ghost" onClick={clearChildOverrides} disabled={saving}>Make all follow client settings</button></>}
            </div>
          )}
          {resolved && (
            <div className="crmr-seqcfg-resolve">
              Currently resolves to <strong>{resolved.source}</strong>
              {resolved.source === 'system' && ', no overrides set anywhere in the chain'}
            </div>
          )}

          <fieldset disabled={!canWrite} className="crmr-seqcfg-fields">
            <OptionGroup label="Tone" options={TONES} value={draft.tone} onChange={v => setDraft({ ...draft, tone: v })} />
            <OptionGroup label="Intent" options={INTENTS} value={draft.intent} onChange={v => setDraft({ ...draft, intent: v })} />
            <OptionGroup label="Length" options={LENGTHS} value={draft.length} onChange={v => setDraft({ ...draft, length: v })} />
            <OptionGroup label="Meeting ask" options={MEETING_ASKS} value={draft.meeting_ask} onChange={v => setDraft({ ...draft, meeting_ask: v })} />
            <NumberChips label="Emails" min={0} max={7} value={draft.email_count} onChange={v => setDraft({ ...draft, email_count: v })} />
            <NumberChips label="LinkedIn messages" min={0} max={7} value={draft.linkedin_count} onChange={v => setDraft({ ...draft, linkedin_count: v })} />
            {draft.email_count === 0 && draft.linkedin_count === 0 && <div className="crmr-seqcfg-hint">At least one email or LinkedIn message is needed.</div>}
            <div className="crmr-seqcfg-grp">
              <div className="crmr-seqcfg-grp-h">
                <span>Steps that carry the meeting ask</span>
                {Array.isArray(draft.sales_steps) && (
                  <button type="button" className="crmr-seqcfg-reset" onClick={() => { const { sales_steps, ...rest } = draft; setDraft(rest); }}>Use default</button>
                )}
              </div>
              <div className="crmr-seqcfg-chips">
                {Array.from({ length: stepCount }, (_, i) => i + 1).map(n => {
                  const on = Array.isArray(draft.sales_steps) && draft.sales_steps.includes(n);
                  return (
                    <button key={n} type="button" className={'crmr-seqcfg-chip crmr-seqcfg-chip--ask' + (on ? ' crmr-seqcfg-chip--on' : '')} onClick={() => toggleStep(n)}>{n}</button>
                  );
                })}
              </div>
              <div className="crmr-seqcfg-hint">
                {Array.isArray(draft.sales_steps)
                  ? (draft.sales_steps.length ? `Steps ${draft.sales_steps.join(', ')} ask for a meeting.` : 'No step asks for a meeting.')
                  : 'Using the default.'}
              </div>
            </div>
            <label className="crmr-seqcfg-check">
              <input type="checkbox" checked={draft.why_now_required !== false} onChange={e => setDraft({ ...draft, why_now_required: e.target.checked })} />
              <span>Require "why now" reference when signals exist</span>
            </label>
          </fieldset>

          {canWrite ? (
            <div className="crmr-seqcfg-actions">
              <button className="crmr-act crmr-act-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
              {config && <button className="crmr-act crmr-act-ghost" onClick={clearOverride} disabled={saving}>{isClient ? 'Clear client settings' : 'Clear override'}</button>}
            </div>
          ) : (
            <div className="crmr-seqcfg-readonly">Your role cannot edit {levelName}-level settings. Read-only.</div>
          )}
        </div>
      )}
    </div>
  );
}

function OptionGroup({ label, options, value, onChange }) {
  const set = value !== undefined && value !== null && value !== '';
  return (
    <div className="crmr-seqcfg-grp">
      <div className="crmr-seqcfg-grp-h">
        <span>{label}</span>
        {set && <button type="button" className="crmr-seqcfg-reset" onClick={() => onChange(undefined)}>Use default</button>}
      </div>
      <div className="crmr-seqcfg-opts">
        {options.map(o => (
          <button key={o.value} type="button" className={'crmr-seqcfg-opt' + (value === o.value ? ' crmr-seqcfg-opt--on' : '')} onClick={() => onChange(o.value)}>
            <span className="crmr-seqcfg-opt-t">{o.label}</span>
            <span className="crmr-seqcfg-opt-d">{o.desc}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function NumberChips({ label, min, max, value, onChange }) {
  const set = value !== undefined && value !== null;
  const nums = [];
  for (let n = min; n <= max; n++) nums.push(n);
  return (
    <div className="crmr-seqcfg-grp">
      <div className="crmr-seqcfg-grp-h">
        <span>{label}</span>
        {set && <button type="button" className="crmr-seqcfg-reset" onClick={() => onChange(undefined)}>Use default</button>}
      </div>
      <div className="crmr-seqcfg-chips">
        {nums.map(n => (
          <button key={n} type="button" className={'crmr-seqcfg-chip' + (value === n ? ' crmr-seqcfg-chip--on' : '')} onClick={() => onChange(n)}>{n}</button>
        ))}
      </div>
    </div>
  );
}
